import type { Service, ServiceConflict } from '@/types/billing';
import { parseTime } from './dateUtils';

export function detectServiceOverlaps(
  serviceTimesByDay: Record<string, Array<{group: string; service: string; start: string; end: string}>>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  i18n: any
): string[] {
  const warnings: string[] = [];
  Object.entries(serviceTimesByDay).forEach(([day, dayServices]) => {
    if (dayServices.length <= 1) return;
    for (let i = 0; i < dayServices.length; i++) {
      for (let j = i + 1; j < dayServices.length; j++) {
        const s1 = dayServices[i];
        const s2 = dayServices[j];
        const start1 = parseTime(s1.start);
        const end1 = parseTime(s1.end);
        const start2 = parseTime(s2.start);
        const end2 = parseTime(s2.end);
        if (!Number.isFinite(start1) || !Number.isFinite(end1) || !Number.isFinite(start2) || !Number.isFinite(end2)) {
          continue;
        }
        const overlaps =
          (start1 >= start2 && start1 < end2) ||
          (end1 > start2 && end1 <= end2) ||
          (start1 <= start2 && end1 >= end2);
        if (!overlaps) continue;
        const sameGroup = s1.group === s2.group;
        const warning = sameGroup
          ? i18n._({
              id: 'contracts.wizard.warnings.timeOverlapSameGroup',
              message: 'Time overlap on {day}: {service1} overlaps with {service2}',
              values: { day, service1: s1.service, service2: s2.service },
            })
          : i18n._({
              id: 'contracts.wizard.warnings.timeOverlap',
              message: 'Time overlap on {day}: {service1} ({group1}) overlaps with {service2} ({group2})',
              values: { day, service1: s1.service, group1: s1.group, service2: s2.service, group2: s2.group },
            });
        if (!warnings.includes(warning)) warnings.push(warning);
      }
    }
  });
  return warnings;
}

export function detectServiceConflicts(
  // `serviceCode` is optional: `PartialServicePatternEntry` documents that a
  // pattern parsed back out of a stored contract can be missing it. Such an
  // entry is skipped rather than grouped under the literal key "undefined".
  servicePattern: Record<string, Record<string, { serviceCode?: string }[]>>,
  availableServices: Service[],
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  i18n: any
): { conflicts: ServiceConflict[]; errors: string[] } {
  const conflicts: ServiceConflict[] = [];
  const errors: string[] = [];
  const servicesByDay: Record<string, Record<string, string[]>> = {};
  Object.entries(servicePattern).forEach(([groupType, groupPattern]) => {
    Object.entries(groupPattern || {}).forEach(([day, dayServices]) => {
      if (!servicesByDay[day]) servicesByDay[day] = {};
      dayServices?.forEach((svc) => {
        const serviceCode = svc.serviceCode;
        if (!serviceCode) return;
        if (!servicesByDay[day][serviceCode]) servicesByDay[day][serviceCode] = [];
        servicesByDay[day][serviceCode].push(groupType);
      });
    });
  });
  Object.entries(servicesByDay).forEach(([day, dayServiceGroups]) => {
    Object.entries(dayServiceGroups).forEach(([serviceCode, groups]) => {
      if (groups.length > 1) {
        const serviceDetail = availableServices.find((s) => s.code === serviceCode);
        const serviceName = serviceDetail?.name || serviceCode;
        conflicts.push({ day, serviceCode, serviceName, conflictingGroups: groups });
        const error = i18n._({
          id: 'contracts.wizard.errors.duplicateService',
          message: 'Invalid: {serviceName} cannot be selected for multiple groups on {day} ({groups})',
          values: { day, serviceName, groups: groups.join(', ') },
        });
        if (!errors.includes(error)) errors.push(error);
      }
    });
  });
  return { conflicts, errors };
}

